<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>

    <StandardNavBar />

    <main>
      <PageHero
        eyebrow="Services"
        title="Maintenance, construction"
        subtitle="support and rope access."
        :lead="services.intro"
        :image="heroImage.src"
        :image-alt="heroImage.alt"
        :image-caption="heroImage.caption"
      />

      <!-- ============ CORE SERVICES ============ -->
      <section id="core-services" class="section-pad core-section shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Core services
            </p>
            <h2 class="display-2">
              The work we are
              <span>called in for most often.</span>
            </h2>
          </div>

          <p class="lead">
            These four scopes make up the bulk of our day-to-day work across commercial, residential
            and industrial properties.
          </p>
        </div>

        <div class="core-grid">
          <article
            v-for="(service, index) in services.core"
            :key="service"
            class="card card-hover core-card"
          >
            <span class="core-number">{{ String(index + 1).padStart(2, "0") }}</span>
            <div class="icon-tile">
              <component :is="coreIcons[index]" />
            </div>
            <h3>{{ service }}</h3>
          </article>
        </div>
      </section>

      <!-- ============ OTHER SERVICES ============ -->
      <section id="other-services" class="other-section">
        <div class="shell">
          <div class="section-head">
            <div>
              <p class="eyebrow">
                <span class="eyebrow-line"></span>
                Other services
              </p>
              <h2 class="display-2">
                Additional scopes we take on,
                <span>as part of a project or on their own.</span>
              </h2>
            </div>

            <p class="lead">
              Some of these support a larger contract; others are engaged directly when a building
              needs a specific piece of work done.
            </p>
          </div>

          <ul class="other-grid">
            <li v-for="(service, index) in services.other" :key="service" class="other-card">
              <span class="other-number">{{ String(index + 1).padStart(2, "0") }}</span>
              <component :is="otherIcons[index]" />
              <strong>{{ service }}</strong>
            </li>
          </ul>
        </div>
      </section>

      <!-- ============ THE WORK ON SITE ============ -->
      <section class="section-pad work-section shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              On site
            </p>
            <h2 class="display-2">
              What these scopes
              <span>actually look like.</span>
            </h2>
          </div>

          <p class="lead">
            A few examples from recent jobs. The full set of project photographs, filterable by
            division, is on the Our Work page.
          </p>
        </div>

        <ul class="work-strip">
          <li v-for="item in serviceStrip" :key="item.id" class="work-tile">
            <img :src="item.src" :alt="item.alt" loading="lazy" />
            <div class="work-tile-meta">
              <strong>{{ item.service }}</strong>
              <span v-if="item.location">{{ item.location }}</span>
            </div>
          </li>
        </ul>

        <RouterLink class="btn btn-ghost work-strip-link" to="/our-work">
          See all project photographs
          <ArrowRight />
        </RouterLink>
      </section>

      <!-- ============ WHERE THE WORK IS DELIVERED ============ -->
      <section class="section-pad routing-section shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              How the work is delivered
            </p>
            <h2 class="display-2">
              Each scope is handled by
              <span>the division best suited to it.</span>
            </h2>
          </div>

          <p class="lead">
            On many projects both divisions work together — conventional access where it is practical,
            rope access where it is not.
          </p>
        </div>

        <div class="routing-grid">
          <RouterLink class="card card-hover routing-card" to="/divisions">
            <div class="routing-head">
              <Building2 />
              <h3>{{ buildingCivilDivision.name }}</h3>
            </div>
            <p>{{ buildingCivilDivision.paragraphs[0] }}</p>
            <span class="text-link">
              About this division
              <ArrowRight />
            </span>
          </RouterLink>

          <RouterLink class="card card-hover routing-card" to="/divisions#rope-access">
            <div class="routing-head">
              <Cable />
              <h3>{{ ropeAccessDivision.name }}</h3>
            </div>
            <p>{{ ropeAccessDivision.intro }}</p>
            <span class="text-link">
              About this division
              <ArrowRight />
            </span>
          </RouterLink>
        </div>
      </section>

      <SectionCta
        eyebrow="Service enquiry"
        heading="Tell us which of these you need."
        body="Send the works required, the building type and the site location. We will advise on the approach, the access method and the next practical step."
      />
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import {
  ArrowRight,
  Blocks,
  Brush,
  Building2,
  Cable,
  ClipboardCheck,
  Droplets,
  Eye,
  HardHat,
  PanelTop,
  Signpost,
  SquareStack,
  Waves,
  Wrench,
} from "lucide-vue-next";

import PageHero from "@/components/PageHero.vue";
import SectionCta from "@/components/SectionCta.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";
import {
  buildingCivilDivision,
  ropeAccessDivision,
  services,
} from "@/content/company-content";
import { pageHeroes, serviceStrip } from "@/content/gallery";

export default {
  name: "ServicesPage",

  components: {
    ArrowRight,
    Building2,
    Cable,
    PageHero,
    SectionCta,
    SiteFooter,
    StandardNavBar,
  },

  data() {
    return {
      services,
      buildingCivilDivision,
      ropeAccessDivision,
      serviceStrip,
      heroImage: pageHeroes.services,

      // One icon per core service, in the same order as services.core
      coreIcons: [Wrench, HardHat, Droplets, Brush],

      // One icon per other service, in the same order as services.other
      otherIcons: [
        ClipboardCheck, // Project management support
        Building2, // Building and civil works
        Blocks, // Installation and dismantling works
        Wrench, // Building minor repairs
        PanelTop, // Window and skylight cleaning
        Waves, // Sealant installation and replacement
        SquareStack, // Cladding and glazing panel replacement
        Eye, // Periodic façade inspection works - PFI
        Signpost, // Signage works
      ],
    };
  },
};
</script>

<style scoped>
/* ---------- core services ---------- */

.core-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 52px;
}

.core-card {
  position: relative;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  padding: 30px 28px;
}

.core-number {
  position: absolute;
  top: 26px;
  right: 26px;
  color: #b6c9db;
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.core-card h3 {
  margin: auto 0 0;
  padding-top: 28px;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 21px;
  line-height: 1.26;
  letter-spacing: -0.035em;
}

/* ---------- other services ---------- */

.other-section {
  position: relative;
  z-index: 2;
  padding: 116px 0;
  border-top: 1px solid rgba(11, 37, 69, 0.08);
  border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  background: #f0f6fc;
}

.other-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 52px 0 0;
  padding: 0;
  list-style: none;
}

.other-card {
  position: relative;
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 18px;
  min-height: 104px;
  padding: 24px 26px;
  border: 1px solid var(--line);
  background: var(--surface);
  box-shadow: var(--shadow-card);
  transition:
    transform 200ms ease,
    border-color 200ms ease;
}

.other-card:hover {
  transform: translateY(-3px);
  border-color: rgba(11, 93, 187, 0.4);
}

.other-card svg {
  width: 24px;
  height: 24px;
  color: var(--blue);
}

.other-number {
  position: absolute;
  top: 16px;
  right: 18px;
  color: #c3d3e2;
  font-family: var(--font-display);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.other-card strong {
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 16px;
  line-height: 1.35;
  letter-spacing: -0.02em;
}

/* ---------- work strip ---------- */

.work-section {
  padding-bottom: 0;
}

.work-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 52px 0 0;
  padding: 0;
  list-style: none;
}

.work-tile {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--line);
  background: var(--navy);
  box-shadow: var(--shadow-card);
}

.work-tile img {
  display: block;
  width: 100%;
  height: 290px;
  object-fit: cover;
  transition: transform 600ms ease;
}

.work-tile:hover img {
  transform: scale(1.05);
}

.work-tile::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(6, 18, 38, 0) 44%, rgba(6, 18, 38, 0.84) 100%);
}

.work-tile-meta {
  position: absolute;
  z-index: 2;
  right: 16px;
  bottom: 15px;
  left: 16px;
}

.work-tile-meta strong {
  display: block;
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 14px;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.work-tile-meta span {
  display: block;
  margin-top: 6px;
  color: #a9c9ec;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}

.work-strip-link {
  margin-top: 22px;
}

/* ---------- routing ---------- */

.routing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 52px;
}

.routing-card {
  display: flex;
  flex-direction: column;
  padding: 32px;
  text-decoration: none;
}

.routing-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.routing-head svg {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  color: var(--blue);
}

.routing-head h3 {
  margin: 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.routing-card p {
  margin: 18px 0 26px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.85;
}

.text-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  color: var(--blue);
  font-size: 13px;
  font-weight: 700;
}

.text-link svg {
  width: 15px;
  height: 15px;
  transition: transform 180ms ease;
}

.routing-card:hover .text-link svg {
  transform: translateX(3px);
}

@media (max-width: 1180px) {
  .core-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .other-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .work-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .core-grid,
  .other-grid,
  .routing-grid {
    grid-template-columns: 1fr;
  }

  .work-tile img {
    height: 230px;
  }

  .core-card {
    min-height: 0;
  }

  .core-card h3 {
    padding-top: 22px;
  }

  .other-section {
    padding: 76px 0;
  }

  .routing-card {
    padding: 26px;
  }
}
</style>
